import { isAdmin, isManager } from '@/common/actionHelper';
import { NoraPermissionV2 } from '@/common/constants/roles';
import { FeatureFlagsContext } from '@/common/featureFlagsWrapper';
import { useAccess } from '@/common/hooks/useAccess';
import {
  Assignment as AssignmentIcon,
  FactCheck as FactCheckIcon,
  People as PeopleIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect, useMemo } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { BulkCaseAssignment } from './BulkCaseAssignment';
import styles from './ControlPanel.module.scss';
import { ManagerTabPanel } from './ManagerTab/ManagerTabPanel';
import { PIAPanel } from './PIAPanel';
import { PlanFPanel } from './PlanFPanel';
import { DigitalOwlPanel } from './DigitalOwlPanel';
import QAWorkflowPanel from './QAWorkflow/QAWorkflowPanel';
import { ReassignUser } from './ReassignUser';
import { useControlPanelSection } from './useControlPanelSection';
import { UserStatus } from './UserStatus';

// Roles allowed by the legacy backend `CAN_UPLOAD_PLAN_F` permission — they may
// use the Plan F Upload section even without broader admin access.
const PLAN_F_LEGACY_ROLES = [
  'UW_ADMIN',
  'UW_CHIEF_UNDERWRITER',
  'UW_UNDERWRITING_MANAGER',
];

interface ControlPanelMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  redirect?: boolean;
  redirectUrl?: string;
  canSee: boolean;
}

export function ControlPanelPage() {
  const { flags } = useContext(FeatureFlagsContext);
  const canAccessAdminSections = useAccess({
    permission: NoraPermissionV2.VIEW_CONTROL_PANEL,
    legacyCheck: r =>
      isAdmin(r) ||
      (isManager(r) && flags.controlPanelUnderwritingManagerEnabled),
  });
  const canUploadPlanF = useAccess({
    permission: 'CAN_UPLOAD_PLAN_F',
    legacyCheck: r => r.some(role => PLAN_F_LEGACY_ROLES.includes(role)),
  });
  const { currentSection, setSection } = useControlPanelSection();

  const visibleMenuItems = useMemo<ControlPanelMenuItem[]>(() => {
    const items: ControlPanelMenuItem[] = [
      {
        id: 'reassign-application',
        label: 'Reassign Application',
        icon: <AssignmentIcon fontSize="small" />,
        canSee:
          canAccessAdminSections &&
          !flags.managerTabEnabled,
      },
      {
        id: 'user-status',
        label: 'User Status',
        icon: <PeopleIcon fontSize="small" />,
        canSee: canAccessAdminSections,
      },
      {
        id: 'pia-panel',
        label: 'PIA Report Upload',
        icon: <UploadIcon fontSize="small" />,
        canSee: canAccessAdminSections && !flags.digitalOwlUploadEnabled,
      },
      {
        id: 'uwspecializations',
        label: 'UW Specializations',
        icon: <AssignmentIcon fontSize="small" />,
        redirect: true,
        redirectUrl: '/workbench/case-assignment',
        canSee: canAccessAdminSections,
      },
      {
        id: 'peer-review',
        label: 'Peer Review',
        icon: <AssignmentIcon fontSize="small" />,
        redirect: true,
        redirectUrl: '/workbench/peer-review',
        canSee: canAccessAdminSections,
      },
      // TODO: Add RBAC page link here
      {
        id: 'qa-workflow',
        label: 'Initiate QA Workflow',
        icon: <FactCheckIcon fontSize="small" />,
        canSee: canAccessAdminSections,
      },
      {
        id: 'plan-f-upload',
        label: 'Plan F Upload',
        icon: <UploadIcon fontSize="small" />,
        canSee: flags.planFUploadEnabled && canUploadPlanF,
      },
      {
        id: 'digital-owl-upload',
        label: 'DigitalOwl Upload',
        icon: <UploadIcon fontSize="small" />,
        canSee: canAccessAdminSections && !!flags.digitalOwlUploadEnabled,
      },
      {
        id: 'manager-tab',
        label: 'Manager Tab',
        icon: <AssignmentIcon fontSize="small" />,
        canSee:
          canAccessAdminSections &&
          flags.managerTabEnabled,
      },
    ];
    return items.filter(item => item.canSee);
  }, [
    canAccessAdminSections,
    canUploadPlanF,
    flags.digitalOwlUploadEnabled,
    flags.managerTabEnabled,
    flags.planFUploadEnabled,
  ]);

  // The section to actually render — falls back to the first visible item when
  // the URL points at a section the user can't see (e.g. a Plan-F-only role
  // landing on the default `reassign-application`).
  const effectiveSection = useMemo(() => {
    if (visibleMenuItems.some(i => i.id === currentSection))
      return currentSection;
    return visibleMenuItems[0]?.id;
  }, [visibleMenuItems, currentSection]);

  useEffect(() => {
    if (effectiveSection && effectiveSection !== currentSection) {
      setSection(effectiveSection);
    }
  }, [effectiveSection, currentSection, setSection]);

  const handleMenuClick = (item: ControlPanelMenuItem) => {
    if (item.redirect) {
      return window.open(item.redirectUrl, '_blank');
    }
    setSection(item.id);
  };

  const renderPage = () => {
    switch (effectiveSection) {
      case 'user-status':
        return <UserStatus />;
      case 'reassign-application':
        return (
          <>
            <ReassignUser />
            <BulkCaseAssignment />
          </>
        );
      case 'pia-panel':
        return <PIAPanel />;
      case 'qa-workflow':
        return <QAWorkflowPanel />;
      case 'plan-f-upload':
        return <PlanFPanel />;
      case 'digital-owl-upload':
        return <DigitalOwlPanel />;
      case 'manager-tab':
        return <ManagerTabPanel />;
      default:
        return null;
    }
  };

  if (visibleMenuItems.length === 0) {
    return (
      <Box className={styles.container}>
        <Typography variant="h1">
          You are not authorized to access this page
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.layout}>
      <Box sx={{ borderRight: '1px solid #e0e0e0' }}>
        <List>
          {visibleMenuItems.map(item => (
            <ListItem
              sx={{
                marginBlock: '4px',
                paddingInline: '12px',
              }}
              key={item.id}
              disablePadding
            >
              <ListItemButton
                onClick={() => handleMenuClick(item)}
                selected={effectiveSection === item.id}
                className={styles.menuItem}
                sx={{
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '14px',
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <ListItemIcon
                    sx={{
                      color: 'primary',
                      minWidth: '24px',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={<span>{item.label}</span>} />
                </Box>

                {item.redirect && (
                  <FaExternalLinkAlt size={10} color="#007AFF" />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Main Content */}
      <Box className={styles.mainContent}>
        <Box className={styles.pageContent}>{renderPage()}</Box>
      </Box>
    </Box>
  );
}
